// src/components/CookieBanner.jsx
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useCookies } from "../contexts/CookiesContext";
import { X, Cookie, ChevronRight, Check } from "lucide-react";
import { Link } from "react-router-dom";
import "../styles/CookieBanner.css";

export default function CookieBanner({ onVisibilityChange }) {
  const { showBanner, acceptAll, rejectAll, acceptCustom } = useCookies();
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({
    essential: true,
    functional: false,
    analytics: false,
    advertising: false,
  });

  useEffect(() => {
    if (showBanner) {
      setTimeout(() => {
        onVisibilityChange?.(true);
      }, 1000);
    } else {
      onVisibilityChange?.(false);
    }
  }, [showBanner, onVisibilityChange]);

  const closeBanner = () => {
    onVisibilityChange?.(false);
  };

  const handleAcceptAll = () => {
    acceptAll();
    closeBanner();
  };

  const handleRejectAll = () => {
    rejectAll();
    closeBanner();
  };

  const handleSavePreferences = () => {
    acceptCustom({
      necessary: true,
      analytics: prefs.analytics || prefs.advertising,
      marketing: prefs.advertising,
      preferences: prefs.functional
    });
    setShowPrefs(false);
    closeBanner();
  };

  const togglePref = (key) => {
    if (key === "essential") return;
    setPrefs({
      ...prefs,
      [key]: !prefs[key],
    });
  };

  const cookieTypes = [
    {
      key: "essential",
      title: "Essential",
      description: "Required for basic functionality",
      alwaysActive: true,
    },
    {
      key: "functional",
      title: "Functional",
      description: "Remember preferences & enhance experience",
      alwaysActive: false,
    },
    {
      key: "analytics",
      title: "Analytics",
      description: "Understand visitor interaction",
      alwaysActive: false,
    },
    {
      key: "advertising",
      title: "Advertising",
      description: "Personalized content and ads",
      alwaysActive: false,
    },
  ];

  useEffect(() => {
    const oldPrefs = localStorage.getItem('cookie_preferences');
    if (oldPrefs) {
      try {
        const parsed = JSON.parse(oldPrefs);
        setPrefs(parsed);
      } catch (error) {
        console.error("Error parsing old preferences:", error);
      }
    }
  }, []);

  return (
    <AnimatePresence>
      {showBanner && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="cookie-backdrop"
            onClick={closeBanner}
          />
          
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="cookie-banner-container"
          >
            <div className="cookie-banner-wrapper">
              <div className="cookie-banner-content">
                <div className="cookie-banner-main">
                  <div className="cookie-icon-wrapper">
                    <Cookie className="cookie-icon" />
                  </div>
                  
                  <p className="cookie-banner-text">
                    We use cookies to enhance your experience. By continuing, you agree to our{" "}
                    <Link to="/cookie-policy" className="cookie-link">Cookie Policy</Link>.
                  </p>

                  <div className="cookie-action-buttons">
                    <button
                      onClick={() => setShowPrefs(true)}
                      className="cookie-preferences-button"
                    >
                      Settings
                    </button>
                    
                    <button
                      onClick={handleRejectAll}
                      className="cookie-reject-button"
                    >
                      Reject
                    </button>
                    
                    <button
                      onClick={handleAcceptAll}
                      className="cookie-accept-button"
                    >
                      Accept
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <AnimatePresence>
            {showPrefs && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="modal-backdrop"
                  onClick={() => setShowPrefs(false)}
                />
                
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                  className="modal-container"
                >
                  <div 
                    className="modal-content"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="modal-header">
                      <div className="modal-header-left">
                        <Cookie className="modal-cookie-icon" />
                        <div>
                          <h2 className="modal-title">Cookie Settings</h2>
                          <p className="modal-subtitle">Customize your preferences</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowPrefs(false)}
                        className="modal-close-button"
                      >
                        <X className="modal-close-icon" />
                      </button>
                    </div>

                    <div className="modal-body">
                      <div className="cookie-categories">
                        {cookieTypes.map((type) => (
                          <div
                            key={type.key}
                            className={`cookie-category ${type.alwaysActive ? 'essential' : ''}`}
                          >
                            <div className="cookie-category-header">
                              <div className="cookie-category-info">
                                <div className="cookie-category-title-wrapper">
                                  <h3 className="cookie-category-title">{type.title}</h3>
                                  {type.alwaysActive && (
                                    <span className="always-active-badge">Required</span>
                                  )}
                                </div>
                                <p className="cookie-category-description">{type.description}</p>
                              </div>
                              
                              {!type.alwaysActive && (
                                <button
                                  onClick={() => togglePref(type.key)}
                                  className={`toggle ${prefs[type.key] ? 'toggle-on' : 'toggle-off'}`}
                                >
                                  <span className="toggle-slider" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="modal-actions">
                        <button
                          onClick={() => {
                            setPrefs({
                              essential: true,
                              functional: false,
                              analytics: false,
                              advertising: false,
                            });
                          }}
                          className="modal-action-button"
                        >
                          Reject All
                        </button>
                        
                        <button
                          onClick={() => {
                            setPrefs({
                              essential: true,
                              functional: true,
                              analytics: true,
                              advertising: true,
                            });
                          }}
                          className="modal-action-button"
                        >
                          Accept All
                        </button>
                      </div>

                      <div className="modal-final-actions">
                        <button
                          onClick={() => setShowPrefs(false)}
                          className="cancel-button"
                        >
                          Cancel
                        </button>
                        
                        <button
                          onClick={handleSavePreferences}
                          className="save-button"
                        >
                          Save Preferences
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
}